/**
 * ColorPickerField Component (issue #739)
 *
 * Tyler Forge `forge-color-picker` adapter for React Hook Form. Replaces the
 * plain `<input type="text">` hex entry previously used for `Tag.color` /
 * `TagCategory.color` with a swatch + hex/RGB/HSV picker.
 *
 * `forge-color-picker` has no content slots and reports its value via the
 * `value` property (hex, always normalized with a leading `#`) plus a
 * `forge-color-picker-change` custom event — not a native `change` event —
 * so this wires both through a ref, mirroring `TeamAutocompleteField`'s
 * `forge-autocomplete-change` adapter rather than passing `value` as a JSX
 * prop.
 *
 * That ref-based wiring is required, not just conventional: the underlying
 * component's `value` setter throws `Error('Invalid hex value provided.')`
 * for any non-null, non-hex string — including `''`. React would otherwise
 * write an empty-string default straight through as an attribute/property
 * and crash the component. The value-sync effect below only ever assigns
 * `null` (which the component treats as "reset to its default black") or a
 * verified-valid hex string.
 */

import { useCallback, useEffect, useRef } from 'react';
import { ForgeColorPicker } from '@tylertech/forge-react';
import type { IColorPickerComponent, IColorPickerChangeEventData } from '@tylertech/forge';
import '@tylertech/forge/color-picker';

/** Matches the hex lengths `forge-color-picker`'s `isValidHex` accepts (3/4/6/8 digits), with or without a leading `#`. */
const VALID_HEX = /^#?[0-9a-f]{3}$|^#?[0-9a-f]{4}$|^#?[0-9a-f]{6}$|^#?[0-9a-f]{8}$/i;

function isValidHex(value: string | undefined): value is string {
  return typeof value === 'string' && VALID_HEX.test(value);
}

/** Normalize to the `#RRGGBB`-style form the rest of the app stores (e.g. `Tag.color`). */
function withHash(hex: string): string {
  return hex.startsWith('#') ? hex : `#${hex}`;
}

export interface ColorPickerFieldProps {
  /** Id assigned to the `forge-color-picker` host element. */
  id: string;
  /** Accessible name — rendered as `aria-label` (the component has no label slot). */
  ariaLabel?: string;
  /** Currently selected color, as a hex string (with or without leading `#`). */
  value?: string;
  /** Called with a `#`-prefixed hex string whenever the picker's value changes. */
  onChange: (hex: string) => void;
}

/**
 * Color field adapter for React Hook Form.
 *
 * @example
 * ```tsx
 * <ColorPickerField
 *   id="tag-color"
 *   ariaLabel="Color"
 *   value={watch('color')}
 *   onChange={(hex) => setValue('color', hex, { shouldValidate: true })}
 * />
 * ```
 */
export function ColorPickerField({
  id,
  ariaLabel,
  value,
  onChange,
}: ColorPickerFieldProps): JSX.Element {
  const colorPickerRef = useRef<IColorPickerComponent | null>(null);

  const handleChange = useCallback(
    (event: Event): void => {
      const detail = (event as CustomEvent<IColorPickerChangeEventData>).detail;
      onChange(withHash(detail.hex));
    },
    [onChange]
  );

  useEffect(() => {
    const el = colorPickerRef.current;
    if (!el) return;
    el.addEventListener('forge-color-picker-change', handleChange);
    return () => el.removeEventListener('forge-color-picker-change', handleChange);
  }, [handleChange]);

  // Keep the host's selected color synced with the controlled `value` prop
  // (edit-mode default load, or a reset from the parent form). See the
  // file-level comment for why this must guard against invalid/empty values
  // rather than assigning them directly.
  useEffect(() => {
    const el = colorPickerRef.current;
    if (!el) return;
    el.value = isValidHex(value) ? value : null;
  }, [value]);

  return (
    <ForgeColorPicker ref={colorPickerRef as React.Ref<unknown>} id={id} aria-label={ariaLabel} />
  );
}
